import React from 'react'

/**
 * High-definition vector chess pieces for Pink Pixel OS.
 * Rendered with crisp strokes and fills to look ultra-sharp on all screens.
 */
export const ChessPiece = ({ piece, className = 'w-full h-full' }) => {
  if (!piece) return null
  const isWhite = piece === piece.toUpperCase()
  const type = piece.toLowerCase()

  const whiteFill = '#f8fafc'
  const whiteStroke = '#0f172a'
  const blackFill = '#1e293b'
  const blackStroke = '#e2e8f0'

  const fill = isWhite ? whiteFill : blackFill
  const stroke = isWhite ? whiteStroke : blackStroke

  switch (type) {
    case 'k':
      return (
        <svg viewBox="0 0 45 45" className={className} aria-label={isWhite ? 'White King' : 'Black King'}>
          <g fill="none" fillRule="evenodd" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22.5 11.63V6M20 8h5" stroke={stroke} strokeLinejoin="miter" />
            <path
              d="m22.5 25c0-4 4.5-7.5 4.5-11.5 0-2.5-2-4.5-4.5-4.5s-4.5 2-4.5 4.5c0 4 4.5 7.5 4.5 11.5"
              fill={fill}
              stroke={stroke}
            />
            <path
              d="M11.5 37c5.5 3.5 16.5 3.5 22 0v-7s9-4.5 6-10.5c-4-1-6.5 6-7.5 3-2.5-1-4.5-1.5-9.5-1.5s-7 0.5-9.5 1.5c-1 3-3.5-4-7.5-3-3 6 6 10.5 6 10.5v7z"
              fill={fill}
              stroke={stroke}
            />
            <path d="M11.5 30c5.5-3 16.5-3 22 0M11.5 33.5c5.5-3 16.5-3 22 0M11.5 37c5.5-3 16.5-3 22 0" stroke={stroke} />
          </g>
        </svg>
      )
    case 'q':
      return (
        <svg viewBox="0 0 45 45" className={className} aria-label={isWhite ? 'White Queen' : 'Black Queen'}>
          <g fill="none" fillRule="evenodd" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path
              d="M9 26c8.5-1.5 21-1.5 27 0l2-12-7 11-4-13.5-4.5 13.5-4.5-13.5-4 13.5-7-11 2 12z"
              fill={fill}
              stroke={stroke}
            />
            <path
              d="M9 26c0 2 1.5 2 2.5 4 2.5 4 1 5.5 1 5.5h20s-1.5-1.5 1-5.5c1-2 2.5-2 2.5-4-8.5-1.5-18.5-1.5-27 0z"
              fill={fill}
              stroke={stroke}
            />
            <path d="M11.5 30c3.5-1 18.5-1 22 0M12 33.5c6-1 15-1 21 0" stroke={stroke} />
            <circle cx="6" cy="12" r="2" fill={fill} stroke={stroke} />
            <circle cx="14" cy="9" r="2" fill={fill} stroke={stroke} />
            <circle cx="22.5" cy="8" r="2" fill={fill} stroke={stroke} />
            <circle cx="31" cy="9" r="2" fill={fill} stroke={stroke} />
            <circle cx="39" cy="12" r="2" fill={fill} stroke={stroke} />
          </g>
        </svg>
      )
    case 'r':
      return (
        <svg viewBox="0 0 45 45" className={className} aria-label={isWhite ? 'White Rook' : 'Black Rook'}>
          <g fill="none" fillRule="evenodd" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path
              d="M9 39h27v-3H9v3zM12 36v-4h21v4H12zM11 14V9h4v2h5V9h5v2h5V9h4v5"
              fill={fill}
              stroke={stroke}
            />
            <path d="M11 14h23l-2.5 18H13.5L11 14z" fill={fill} stroke={stroke} />
            <path d="M12 32h21M13.5 28h18M14 24h17M14.5 20h16M15 16h15" stroke={stroke} />
          </g>
        </svg>
      )
    case 'b':
      return (
        <svg viewBox="0 0 45 45" className={className} aria-label={isWhite ? 'White Bishop' : 'Black Bishop'}>
          <g fill="none" fillRule="evenodd" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <g fill={fill} stroke={stroke}>
              <path d="M9 36c3.39-.97 10.11.43 13.5-2 3.39 2.43 10.11 1.03 13.5 2 0 0 1.65.54 3 2-.68.97-1.65.99-3 .5-3.39-.97-10.11.46-13.5-1-3.39 1.46-10.11.03-13.5 1-1.35.49-2.32.47-3-.5 1.35-1.46 3-2 3-2z" />
              <path d="M15 32c2.5 2.5 12.5 2.5 15 0 .5-1.5 0-2 0-2 0-2.5-2.5-4-2.5-4 5.5-1.5 6-11.5-5-15.5-11 4-10.5 14-5 15.5 0 0-2.5 1.5-2.5 4 0 0-.5.5 0 2z" />
              <circle cx="22.5" cy="8.5" r="1.5" />
            </g>
            <path d="M17.5 26h10M15 30h15M22.5 15.5v5M20 18h5" stroke={stroke} />
          </g>
        </svg>
      )
    case 'n':
      return (
        <svg viewBox="0 0 45 45" className={className} aria-label={isWhite ? 'White Knight' : 'Black Knight'}>
          <g fill="none" fillRule="evenodd" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path
              d="M22 10c10.5 1 16.5 8 16 29H15c0-9 10-6.5 8-21"
              fill={fill}
              stroke={stroke}
            />
            <path
              d="M24 18c.38 2.91-5.55 7.37-8 9-3 2-2.82 4.34-5 4-1.042-.94 1.41-3.04 0-3-1 0-.64 1.42-2 1-1.042-.32-.977-1.14 0-2 1-.87 2-2 2-3 0-1-2-1-2-1s1.39-1.29 2-3c1-2.5-1-4-1-4 .87.87 3.32 1.31 4 0 1-2 1-3 1-3s1 1 2 0c1-1 1.5-3 2.5-4 1-1 2.5-.5 3.5 0 1.5.75 3.5 2 3.5 2z"
              fill={fill}
              stroke={stroke}
            />
            <circle cx="9.5" cy="25.5" r="1" fill={stroke} />
            <path d="M15 15.5c-1 1.5-.5 3.5 1 4" stroke={stroke} />
          </g>
        </svg>
      )
    case 'p':
    default:
      return (
        <svg viewBox="0 0 45 45" className={className} aria-label={isWhite ? 'White Pawn' : 'Black Pawn'}>
          <path
            d="m22.5 9c-2.21 0-4 1.79-4 4 0 .89.29 1.71.78 2.38C17.33 16.5 16 18.59 16 21c0 2.03.94 3.84 2.41 5.03-3 1.06-7.41 5.55-7.41 13.47h23c0-7.92-4.41-12.41-7.41-13.47 1.47-1.19 2.41-3 2.41-5.03 0-2.41-1.33-4.5-3.28-5.62.49-.67.78-1.49.78-2.38 0-2.21-1.79-4-4-4z"
            fill={fill}
            stroke={stroke}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      )
  }
}
